/**
 * Perfil de aluno: o que treina, com o que tem, no tempo que tem.
 *
 * Tudo é opcional. A tela existe para o profissional montar treino que caiba na
 * vida de quem vai fazer — e um formulário obrigatório na segunda visita ao
 * produto afasta mais do que informa.
 *
 * A única exceção de tom é o campo de restrições. Ele pergunta sobre saúde, e
 * o texto diz para quem aquilo vai: o profissional vinculado, ninguém mais.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/Alert';
import { ChipGroup } from '@/components/ChipGroup';
import {
  EQUIPMENT,
  EXPERIENCE_LEVELS,
  GOALS,
  LOCATIONS,
  MODALITIES,
  SESSION_MINUTES,
  type Equipment,
  type ExperienceLevel,
  type Goal,
  type Modality,
  type TrainingLocation,
} from '@/features/profiles/api';
import { ruleKey, useSaveStudentProfile, useStudentProfile } from '@/features/profiles/useProfiles';

const MAX_RESTRICTIONS = 2000;

export default function StudentProfilePage() {
  const { t } = useTranslation(['profiles', 'common']);
  const { data, isPending, isError } = useStudentProfile();
  const mutation = useSaveStudentProfile();

  const [goals, setGoals] = useState<Goal[]>([]);
  const [level, setLevel] = useState<ExperienceLevel>('beginner');
  const [minutes, setMinutes] = useState<number[]>([]);
  const [locations, setLocations] = useState<TrainingLocation[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [modalities, setModalities] = useState<Modality[]>([]);
  const [restrictions, setRestrictions] = useState('');

  // Preenche o formulário quando a resposta chega, e também depois de gravar —
  // o servidor normaliza (remove repetição, reordena), e a tela precisa mostrar
  // o que ficou gravado, não o que foi digitado.
  useEffect(() => {
    if (!data) return;
    setGoals(data.goals);
    setLevel(data.experience_level);
    setMinutes(data.available_minutes);
    setLocations(data.locations);
    setEquipment(data.equipment);
    setModalities(data.modalities);
    setRestrictions(data.restrictions_text ?? '');
  }, [data]);

  if (isPending) {
    return (
      <div className="px-5 py-6" aria-busy="true">
        <p className="text-(--color-sand-700)">{t('common:state.loading')}</p>
      </div>
    );
  }

  function handleSubmit() {
    mutation.mutate({
      goals,
      experience_level: level,
      available_minutes: minutes,
      locations,
      equipment,
      modalities,
      restrictions_text: restrictions.trim() || null,
    });
  }

  const labelled = <T extends string>(values: readonly T[], group: string) =>
    values.map((value) => ({ value, label: t(`profiles:${group}.${value}`) }));

  return (
    <div className="flex flex-col gap-6 px-5 py-6">
      <header>
        <h1 className="text-3xl">{t('profiles:student.title')}</h1>
        <p className="mt-1 text-(--color-sand-700)">{t('profiles:student.subtitle')}</p>
      </header>

      {isError ? <Alert tone="error">{t('profiles:loadFailed')}</Alert> : null}

      <ChipGroup
        legend={t('profiles:student.goals')}
        options={labelled(GOALS, 'goal')}
        selected={goals}
        onChange={setGoals}
      />

      <ChipGroup
        legend={t('profiles:student.level')}
        options={labelled(EXPERIENCE_LEVELS, 'level')}
        selected={[level]}
        onChange={(next) => setLevel(next[0] ?? 'beginner')}
        multiple={false}
      />

      <ChipGroup
        legend={t('profiles:student.modalities')}
        options={labelled(MODALITIES, 'modality')}
        selected={modalities}
        onChange={setModalities}
      />

      <ChipGroup
        legend={t('profiles:student.minutes')}
        hint={t('profiles:student.minutesHint')}
        // `value`, não `count`: com `count` o i18next procuraria forma plural, e
        // "15 min" não tem singular nem plural em nenhum dos três idiomas.
        options={SESSION_MINUTES.map((value) => ({
          value,
          label: t('profiles:minutes', { value }),
        }))}
        selected={minutes}
        onChange={setMinutes}
      />

      <ChipGroup
        legend={t('profiles:student.locations')}
        options={labelled(LOCATIONS, 'location')}
        selected={locations}
        onChange={setLocations}
      />

      <ChipGroup
        legend={t('profiles:student.equipment')}
        options={labelled(EQUIPMENT, 'equipment')}
        selected={equipment}
        onChange={setEquipment}
      />

      <div className="mh-field">
        <label htmlFor="restrictions">{t('profiles:student.restrictions')}</label>
        {/* O texto do aviso é deliberado: diz quem lê e para que serve, antes
            de a pessoa escrever. Perguntar sobre saúde sem dizer o destino do
            que foi escrito é o que faz alguém deixar em branco por precaução. */}
        <p className="mb-2 text-sm text-(--color-sand-700)">
          {t('profiles:student.restrictionsHint')}
        </p>
        <textarea
          id="restrictions"
          rows={4}
          maxLength={MAX_RESTRICTIONS}
          value={restrictions}
          onChange={(event) => setRestrictions(event.target.value)}
        />
      </div>

      {mutation.isError ? (
        <Alert tone="error" live="assertive">
          {t(ruleKey(mutation.error))}
        </Alert>
      ) : null}

      {mutation.isSuccess ? <Alert tone="success">{t('profiles:saved')}</Alert> : null}

      <button
        type="button"
        className="mh-button mh-button--primary w-full"
        disabled={mutation.isPending}
        onClick={handleSubmit}
      >
        {mutation.isPending ? t('common:state.loading') : t('profiles:save')}
      </button>
    </div>
  );
}
